import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Modal from '@medflex/components/stories/Modal';
import Button from '@medflex/components/stories/Button';
import Brand from '@medflex/components/stories/Brand';
import {
  IconInformationOutline,
  IconQuestion,
  IconCheveronDown,
  IconCheveronUp,
  IconCheckmarkOutline,
} from '@medflex/components/stories/Icons';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@medflex/utils/Tracking';
import saveToClipboard from '@medflex/utils/saveToClipboard';
import config from '@medflex/utils/config';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import useUserProfile from '../hooks/useUserProfile';

const PhysicianCodeSnippetsModule = () => {
  const isWideScreen = useIsWideScreen();
  const { userProfile } = useUserProfile();
  const history = useHistory();
  const { t } = useTranslation('physician');
  const [pagesLink, setPagesLink] = useState();
  const [widgetIndex, setWidgetIndex] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [slug, setSlug] = useState();
  const [codeRefs, setCodeRefs] = useState({});
  const [supportRemarkHidden, setSupportRemarkHidden] = useState(true);

  const BASIC = 'border flex flex-col p-8 relative';
  const COLLAPSED = `${BASIC} hover:bg-slate-50 cursor-pointer`;
  const EXPANDED = `${BASIC} bg-slate-50`;

  const types = ['small', 'medium', 'large', 'bubble', 'pam__small', 'pam__large1', 'pam__large2'];

  const copyLink = (event, { index }) => {
    saveToClipboard(codeRefs[index].current);
    trackEvent(event);
    setIsCopied(true);
  };

  useEffect(() => {
    const { slugWithId } = userProfile.attributes;
    setSlug(slugWithId);
    setPagesLink(`${config.PHYSICIAN_PAGES_URL}/${slugWithId}`);
  }, [userProfile]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (slug) {
      const script = document.createElement('script');

      script.src = `${config.PHYSICIAN_PAGES_URL}/widget/v2.js`;
      script.async = true;

      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [slug]);

  useEffect(() => {
    setSupportRemarkHidden(true);
  }, [widgetIndex]);

  const buttons = types.map(
    type => `<medflex-widget type="${type}" href="${config.PHYSICIAN_PAGES_URL}/${slug}" />`,
  );

  useEffect(() => {
    setCodeRefs(buttons.map(() => React.createRef(), {}));
  }, [widgetIndex]);

  const handleClick = (event, { index }) => {
    if (widgetIndex !== index) {
      trackEvent(event, { actionText: `Widget ${types[index]} ausklappen` });
      setIsCopied(false);
      setWidgetIndex(index);
    }
  };

  const createMarkup = ({ index }) => ({ __html: buttons[index] });

  if (isWideScreen) {
    return (
      <Modal
        className="flex flex-col min-h-0 overflow-auto scrolling-touch px-4"
        showCloseButton
        isFullScreen
        onClickCloseButton={() => history.push('/settings/infoMaterialForPatients')}
      >
        <div data-component="CodeSnippetsModal">
          <header className="flex mb-4 items-center justify-center">
            <h2 className="text-2xl lg:text-3xl font-semibold">{t('code_snippets.title')}</h2>
            <a
              href="https://medflex.de/hilfe/behandler-patientenanfrage-management/patientenanfrage-modul-einrichten/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex text-blue-500 ml-2"
            >
              <IconInformationOutline size="6" />
            </a>
          </header>
          <p>
            {t('code_snippets.info_text', {
              allowHtml: true,
              pagesLink,
            })}
          </p>
          <div className="text-lg mt-8 mb-4">
            <span className="text-slate-600">{t('code_snippets.step_one.title')}</span>
            <span>{t('code_snippets.step_one.title_text')}</span>
          </div>
          {buttons.map((button, index) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <div
              key={index}
              onClick={event => handleClick(event, { index })}
              className={widgetIndex !== index ? COLLAPSED : EXPANDED}
            >
              <div className="pointer-events-none flex justify-center items-center select-none">
                {/* eslint-disable-next-line react/no-danger */}
                <div dangerouslySetInnerHTML={createMarkup({ index })} />
              </div>
              {index !== widgetIndex ? (
                <IconCheveronDown className="absolute right-0 inset-0-auto mr-8 fill-current text-slate-400" />
              ) : (
                <IconCheveronUp
                  className="absolute right-0 inset-0-auto mr-8 fill-current text-slate-400 cursor-pointer hover:text-slate-600"
                  onClick={() => setWidgetIndex(null)}
                />
              )}
              {widgetIndex === index && (
                <>
                  <div className="text-lg mt-8 mb-4">
                    <span className="text-slate-600">{t('code_snippets.step_two.title')}</span>
                    <span>{t('code_snippets.step_two.title_text')}</span>
                  </div>
                  <p>{t('code_snippets.step_two.info_text')}</p>
                  <div className="border border-slate-300 w-full mb-2 p-2 mt-4">
                    <div className="text-slate-500 break-all" ref={codeRefs[index]}>
                      <p>{button}</p>
                      <p>{`<script src="${config.PHYSICIAN_PAGES_URL}/widget/v2.js"></script>`}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Button
                      kind="primary:outline"
                      onClick={() => {
                        window.open(
                          'https://medflex.de/hilfe/behandler-patientenanfrage-management/patientenanfrage-modul-einrichten/',
                        );
                      }}
                    >
                      {t('code_snippets.step_two.info_link')}
                    </Button>
                    <Button
                      isActive={isCopied}
                      className="mt-4 lg:mt-0 lg:ml-4"
                      onClick={event => copyLink(event, { index })}
                    >
                      {isCopied && <IconCheckmarkOutline className="mr-2" />}
                      {isCopied
                        ? t('code_snippets.step_two.copied')
                        : t('code_snippets.step_two.copy_code')}
                    </Button>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="flex items-center mt-12 text-blue-500 font-semibold"
                      onClick={() => setSupportRemarkHidden(!supportRemarkHidden)}
                    >
                      <IconQuestion size="text" />
                      <span className="ml-2">{t('code_snippets.support_remark.trigger')}</span>
                    </button>

                    <article className="mt-2 pl-6" hidden={supportRemarkHidden}>
                      <p>{t('code_snippets.support_remark.description')}</p>
                      <p className="mt-2">
                        {t('code_snippets.support_remark.instruction', { allowHtml: true })}
                      </p>
                    </article>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </Modal>
    );
  }

  return (
    <Modal isFullScreen>
      <div className="w-full flex flex-col items-center p-8 text-black mt-12">
        <Brand size="20" />
        <h1 className="text-2xl mt-8 text-center">{t('patient_cases.mobile.title')}</h1>
        <Button
          kind="primary:outline"
          onClick={() => {
            history.push('/');
          }}
          className="my-8"
        >
          {t('patient_cases.mobile.button_label')}
        </Button>
      </div>
    </Modal>
  );
};
export default PhysicianCodeSnippetsModule;
